import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserPlan } from "@/hooks/useUserPlan";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Shield,
  Calculator,
  DollarSign,
  Save,
  RefreshCw,
  Users,
  Zap,
  Loader2,
  Settings,
} from "lucide-react";

// Gemini 2.5 Flash pricing (per 1M tokens) - used for questions
const FLASH_INPUT_COST_PER_1M = 0.075; // USD
const FLASH_OUTPUT_COST_PER_1M = 0.30; // USD

// Gemini 2.5 Pro pricing (per 1M tokens) - used for document generation
const PRO_INPUT_COST_PER_1M = 1.25; // USD
const PRO_OUTPUT_COST_PER_1M = 10.00; // USD

// Average tokens per character (rough estimate for Portuguese)
const TOKENS_PER_CHAR = 0.35;

interface CostParams {
  avgQuestionSizeChars: number;
  avgAnswerSizeChars: number;
  avgContextSizeChars: number;
  avgQuestionsPerForm: number;
  avgLeadsPerForm: number;
  avgFormsPerUser: number;
  profitMargin: number;
  // Document generation params
  avgDocInputChars: number;
  avgDocOutputWords: number; // Now in words instead of chars
}

interface TopUserCost {
  email: string;
  totalLeads: number;
  estimatedCostUSD: number;
  estimatedCostBRL: number;
}

// Average word limit (default from form builder)
const DEFAULT_WORD_LIMIT = 2000;
const AVG_CHARS_PER_WORD = 6; // Portuguese averages ~6 chars per word

const DEFAULT_PARAMS: CostParams = {
  avgQuestionSizeChars: 200,
  avgAnswerSizeChars: 150,
  avgContextSizeChars: 1500,
  avgQuestionsPerForm: 5,
  avgLeadsPerForm: 10,
  avgFormsPerUser: 2,
  profitMargin: 100,
  avgDocInputChars: 3000,
  avgDocOutputWords: DEFAULT_WORD_LIMIT,
};

const USD_TO_BRL = 5.5; // Exchange rate

export default function AdminCosts() {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const { role, isLoading: planLoading } = useUserPlan();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [params, setParams] = useState<CostParams>(DEFAULT_PARAMS);
  const [savedParams, setSavedParams] = useState<CostParams>(DEFAULT_PARAMS);
  const [topUsers, setTopUsers] = useState<TopUserCost[]>([]);
  const [platformAverages, setPlatformAverages] = useState<Partial<CostParams>>({});
  
  // Stripe settings state
  const [proLeadLimit, setProLeadLimit] = useState(3000);
  const [looseLeadsPrice, setLooseLeadsPrice] = useState(100); // cents (R$1.00)
  const [savingProPlan, setSavingProPlan] = useState(false);
  const [savingLooseLeads, setSavingLooseLeads] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/auth");
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!authLoading && !planLoading && isAuthenticated && role !== "admin") {
      toast({
        title: "Acesso negado",
        description: "Você não tem permissão para acessar esta página.",
        variant: "destructive",
      });
      navigate("/dashboard");
    }
  }, [authLoading, planLoading, isAuthenticated, role, navigate, toast]);

  useEffect(() => {
    if (isAuthenticated && role === "admin") {
      loadData();
    }
  }, [isAuthenticated, role]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load saved params from localStorage
      const saved = localStorage.getItem("admin_cost_params");
      if (saved) {
        const parsed = JSON.parse(saved);
        setParams(parsed);
        setSavedParams(parsed);
      }

      // Load platform settings from database
      const { data: settings } = await supabase
        .from("platform_settings")
        .select("key, value");
      
      if (settings) {
        settings.forEach(s => {
          if (s.key === "pro_lead_limit") {
            setProLeadLimit(Number(s.value));
          }
          if (s.key === "loose_leads_price_brl") {
            setLooseLeadsPrice(Number(s.value));
          }
        });
      }

      // Calculate platform averages
      await calculatePlatformAverages();
      
      // Calculate top users
      calculateTopUsers();
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePlatformAverages = async () => {
    try {
      // Get all form responses to calculate averages
      const { data: responses } = await supabase
        .from("form_responses")
        .select("question, answer, is_ai_generated")
        .eq("is_ai_generated", true)
        .limit(500);

      // Get forms data
      const { data: forms } = await supabase
        .from("forms")
        .select("id, user_id, ai_question_count, ai_final_prompt");

      // Get sessions data
      const { data: sessions } = await supabase
        .from("form_sessions")
        .select("id, form_id");

      // Get profiles to identify top 20 users
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, email");

      if (responses && responses.length > 0) {
        const avgQuestionSize = responses.reduce((acc, r) => acc + r.question.length, 0) / responses.length;
        const avgAnswerSize = responses.reduce((acc, r) => acc + r.answer.length, 0) / responses.length;
        
        setPlatformAverages({
          avgQuestionSizeChars: Math.round(avgQuestionSize),
          avgAnswerSizeChars: Math.round(avgAnswerSize),
        });
      }

      if (forms && forms.length > 0) {
        const avgQuestionsPerForm = forms.reduce((acc, f) => acc + (f.ai_question_count || 5), 0) / forms.length;
        
        // Calculate forms per user
        const userFormCounts: Record<string, number> = {};
        forms.forEach(f => {
          userFormCounts[f.user_id] = (userFormCounts[f.user_id] || 0) + 1;
        });
        const avgFormsPerUser = Object.keys(userFormCounts).length > 0
          ? Object.values(userFormCounts).reduce((a, b) => a + b, 0) / Object.keys(userFormCounts).length
          : 2;

        setPlatformAverages(prev => ({
          ...prev,
          avgQuestionsPerForm: Math.round(avgQuestionsPerForm * 10) / 10,
          avgFormsPerUser: Math.round(avgFormsPerUser * 10) / 10,
        }));
      }

      if (sessions && forms && forms.length > 0) {
        // Calculate leads per form
        const formSessionCounts: Record<string, number> = {};
        sessions.forEach(s => {
          formSessionCounts[s.form_id] = (formSessionCounts[s.form_id] || 0) + 1;
        });
        const avgLeadsPerForm = Object.keys(formSessionCounts).length > 0
          ? Object.values(formSessionCounts).reduce((a, b) => a + b, 0) / Object.keys(formSessionCounts).length
          : 10;

        setPlatformAverages(prev => ({
          ...prev,
          avgLeadsPerForm: Math.round(avgLeadsPerForm * 10) / 10,
        }));
      }

      // Calculate document input size from top 20 users
      if (profiles && forms && sessions && responses) {
        // Calculate leads per user
        const userLeadCounts: Record<string, number> = {};
        profiles.forEach(p => {
          const userForms = forms.filter(f => f.user_id === p.user_id);
          const userFormIds = userForms.map(f => f.id);
          const userSessions = sessions.filter(s => userFormIds.includes(s.form_id));
          userLeadCounts[p.user_id] = userSessions.length;
        });

        // Get top 20 user IDs by lead count
        const top20UserIds = Object.entries(userLeadCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 20)
          .map(([userId]) => userId);

        // Calculate average document input for top 20 users
        // Input = ai_final_prompt + all Q&A pairs for the session
        const top20Forms = forms.filter(f => top20UserIds.includes(f.user_id));
        const top20FormIds = top20Forms.map(f => f.id);
        const top20Sessions = sessions.filter(s => top20FormIds.includes(s.form_id));
        const top20SessionIds = top20Sessions.map(s => s.id);

        // Get responses for top 20 sessions
        const { data: top20Responses } = await supabase
          .from("form_responses")
          .select("session_id, question, answer")
          .in("session_id", top20SessionIds.slice(0, 100)); // Limit to avoid too large query

        if (top20Responses && top20Responses.length > 0) {
          // Group responses by session
          const sessionResponses: Record<string, { question: string; answer: string }[]> = {};
          top20Responses.forEach(r => {
            if (!sessionResponses[r.session_id]) {
              sessionResponses[r.session_id] = [];
            }
            sessionResponses[r.session_id].push({ question: r.question, answer: r.answer });
          });

          // Calculate average input size per session (prompt + all Q&A)
          let totalInputChars = 0;
          let sessionCount = 0;

          Object.entries(sessionResponses).forEach(([sessionId, qaPairs]) => {
            const session = top20Sessions.find(s => s.id === sessionId);
            if (session) {
              const form = top20Forms.find(f => f.id === session.form_id);
              const promptSize = form?.ai_final_prompt?.length || 500;
              const qaSize = qaPairs.reduce((acc, qa) => acc + qa.question.length + qa.answer.length, 0);
              totalInputChars += promptSize + qaSize;
              sessionCount++;
            }
          });

          if (sessionCount > 0) {
            const avgDocInput = Math.round(totalInputChars / sessionCount);
            setPlatformAverages(prev => ({
              ...prev,
              avgDocInputChars: avgDocInput,
              avgDocOutputWords: DEFAULT_WORD_LIMIT,
            }));
          }
        }
      }
    } catch (error) {
      console.error("Error calculating averages:", error);
    }
  };

  const calculateTopUsers = async () => {
    try {
      // Get all profiles
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, email");

      // Get all forms
      const { data: forms } = await supabase
        .from("forms")
        .select("id, user_id, ai_question_count");

      // Get all sessions
      const { data: sessions } = await supabase
        .from("form_sessions")
        .select("id, form_id");

      if (!profiles || !forms || !sessions) return;

      // Calculate costs per user
      const userCosts: TopUserCost[] = profiles.map(profile => {
        const userForms = forms.filter(f => f.user_id === profile.user_id);
        const userFormIds = userForms.map(f => f.id);
        const userSessions = sessions.filter(s => userFormIds.includes(s.form_id));
        
        // Calculate estimated cost based on sessions and form configurations
        let questionsInputTokens = 0;
        let questionsOutputTokens = 0;
        let docInputTokens = 0;
        let docOutputTokens = 0;

        userForms.forEach(form => {
          const formSessions = sessions.filter(s => s.form_id === form.id);
          const questionsPerLead = form.ai_question_count || params.avgQuestionsPerForm;
          
          formSessions.forEach(() => {
            // For each question in a session (uses Flash model)
            for (let q = 0; q < questionsPerLead; q++) {
              const contextTokens = params.avgContextSizeChars * TOKENS_PER_CHAR;
              const previousQATokens = q * (params.avgQuestionSizeChars + params.avgAnswerSizeChars) * TOKENS_PER_CHAR;
              const currentAnswerTokens = params.avgAnswerSizeChars * TOKENS_PER_CHAR;
              
              questionsInputTokens += contextTokens + previousQATokens + currentAnswerTokens;
              questionsOutputTokens += params.avgQuestionSizeChars * TOKENS_PER_CHAR;
            }

            // Add document generation cost per session (uses Pro model)
            docInputTokens += params.avgDocInputChars * TOKENS_PER_CHAR;
            docOutputTokens += params.avgDocOutputWords * AVG_CHARS_PER_WORD * TOKENS_PER_CHAR;
          });
        });

        // Flash model for questions
        const questionsCostUSD = (questionsInputTokens / 1_000_000) * FLASH_INPUT_COST_PER_1M + 
                                  (questionsOutputTokens / 1_000_000) * FLASH_OUTPUT_COST_PER_1M;
        
        // Pro model for documents
        const docCostUSD = (docInputTokens / 1_000_000) * PRO_INPUT_COST_PER_1M + 
                           (docOutputTokens / 1_000_000) * PRO_OUTPUT_COST_PER_1M;
        
        const totalCostUSD = questionsCostUSD + docCostUSD;

        return {
          email: profile.email,
          totalLeads: userSessions.length,
          estimatedCostUSD: totalCostUSD,
          estimatedCostBRL: totalCostUSD * USD_TO_BRL,
        };
      });

      // Sort by cost and take top 20
      const sorted = userCosts
        .sort((a, b) => b.estimatedCostUSD - a.estimatedCostUSD)
        .slice(0, 20);

      setTopUsers(sorted);
    } catch (error) {
      console.error("Error calculating top users:", error);
    }
  };

  const calculateCostPerLead = (): { 
    questionsCostUSD: number; 
    questionsCostBRL: number;
    docCostUSD: number;
    docCostBRL: number;
    costUSD: number; 
    costBRL: number;
  } => {
    // Calculate cost for a single lead going through the full form
    let totalInputTokens = 0;
    let totalOutputTokens = 0;

    // Cost for generating questions
    for (let q = 0; q < params.avgQuestionsPerForm; q++) {
      // Input: context + previous Q&A pairs + current answer
      const contextTokens = params.avgContextSizeChars * TOKENS_PER_CHAR;
      const previousQATokens = q * (params.avgQuestionSizeChars + params.avgAnswerSizeChars) * TOKENS_PER_CHAR;
      const currentAnswerTokens = params.avgAnswerSizeChars * TOKENS_PER_CHAR;
      
      totalInputTokens += contextTokens + previousQATokens + currentAnswerTokens;
      
      // Output: just the generated question
      totalOutputTokens += params.avgQuestionSizeChars * TOKENS_PER_CHAR;
    }

    // Questions use Flash model
    const questionsInputCostUSD = (totalInputTokens / 1_000_000) * FLASH_INPUT_COST_PER_1M;
    const questionsOutputCostUSD = (totalOutputTokens / 1_000_000) * FLASH_OUTPUT_COST_PER_1M;
    const questionsTotalCostUSD = questionsInputCostUSD + questionsOutputCostUSD;

    // Cost for generating final document (uses Pro model)
    const docInputTokens = params.avgDocInputChars * TOKENS_PER_CHAR;
    const docOutputTokens = params.avgDocOutputWords * AVG_CHARS_PER_WORD * TOKENS_PER_CHAR;
    const docInputCostUSD = (docInputTokens / 1_000_000) * PRO_INPUT_COST_PER_1M;
    const docOutputCostUSD = (docOutputTokens / 1_000_000) * PRO_OUTPUT_COST_PER_1M;
    const docTotalCostUSD = docInputCostUSD + docOutputCostUSD;

    const totalCostUSD = questionsTotalCostUSD + docTotalCostUSD;

    return {
      questionsCostUSD: questionsTotalCostUSD,
      questionsCostBRL: questionsTotalCostUSD * USD_TO_BRL,
      docCostUSD: docTotalCostUSD,
      docCostBRL: docTotalCostUSD * USD_TO_BRL,
      costUSD: totalCostUSD,
      costBRL: totalCostUSD * USD_TO_BRL,
    };
  };

  const calculateMonthlyUserCost = (): { costUSD: number; costBRL: number } => {
    const leadCost = calculateCostPerLead();
    const leadsPerMonth = params.avgLeadsPerForm * params.avgFormsPerUser;
    
    return {
      costUSD: leadCost.costUSD * leadsPerMonth,
      costBRL: leadCost.costBRL * leadsPerMonth,
    };
  };

  const calculateWithMargin = (cost: number): number => {
    return cost * (1 + params.profitMargin / 100);
  };

  const handleSaveParams = () => {
    localStorage.setItem("admin_cost_params", JSON.stringify(params));
    setSavedParams(params);
    toast({ title: "Parâmetros salvos!" });
    calculateTopUsers();
  };

  const handleResetToAverages = () => {
    const newParams: CostParams = {
      ...DEFAULT_PARAMS,
      avgQuestionSizeChars: platformAverages.avgQuestionSizeChars || DEFAULT_PARAMS.avgQuestionSizeChars,
      avgAnswerSizeChars: platformAverages.avgAnswerSizeChars || DEFAULT_PARAMS.avgAnswerSizeChars,
      avgQuestionsPerForm: platformAverages.avgQuestionsPerForm || DEFAULT_PARAMS.avgQuestionsPerForm,
      avgLeadsPerForm: platformAverages.avgLeadsPerForm || DEFAULT_PARAMS.avgLeadsPerForm,
      avgFormsPerUser: platformAverages.avgFormsPerUser || DEFAULT_PARAMS.avgFormsPerUser,
      avgDocInputChars: platformAverages.avgDocInputChars || DEFAULT_PARAMS.avgDocInputChars,
      avgDocOutputWords: platformAverages.avgDocOutputWords || DEFAULT_PARAMS.avgDocOutputWords,
    };
    setParams(newParams);
    toast({ title: "Parâmetros resetados para médias da plataforma" });
  };

  const handleSaveProPlan = async () => {
    setSavingProPlan(true);
    try {
      const { data, error } = await supabase.functions.invoke("update-stripe-settings", {
        body: { action: "update_pro_plan", proLeadLimit },
      });
      
      if (error) throw error;
      
      toast({ 
        title: "Plano Pro atualizado!", 
        description: `Limite de ${proLeadLimit} leads/mês configurado no Stripe.`
      });
    } catch (error) {
      console.error("Error updating Pro plan:", error);
      toast({ 
        title: "Erro ao atualizar", 
        description: "Não foi possível atualizar o Stripe.",
        variant: "destructive" 
      });
    } finally {
      setSavingProPlan(false);
    }
  };

  const handleSaveLooseLeads = async () => {
    setSavingLooseLeads(true);
    try {
      const { data, error } = await supabase.functions.invoke("update-stripe-settings", {
        body: { action: "update_loose_leads", looseLeadsPrice },
      });
      
      if (error) throw error;
      
      toast({ 
        title: "Preço de leads avulsos atualizado!", 
        description: `Novo preço: R$ ${(looseLeadsPrice / 100).toFixed(2)} por 100 leads.`
      });
    } catch (error) {
      console.error("Error updating loose leads:", error);
      toast({ 
        title: "Erro ao atualizar", 
        description: "Não foi possível atualizar o Stripe.",
        variant: "destructive" 
      });
    } finally {
      setSavingLooseLeads(false);
    }
  };

  const leadCost = calculateCostPerLead();
  const monthlyUserCost = calculateMonthlyUserCost();
  const totalPlatformCost = topUsers.reduce((acc, u) => acc + u.estimatedCostUSD, 0);

  // Calculate suggested lead limit based on cost
  const suggestedProLeads = Math.floor(29.90 / calculateWithMargin(leadCost.costBRL));
  const suggestedLooseLeadsPrice50 = Math.round(calculateWithMargin(leadCost.costBRL) * 50 * 100); // in cents

  if (authLoading || planLoading || role !== "admin") {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/admin">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-gradient-primary rounded-xl flex items-center justify-center">
                <Calculator className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="text-xl font-display font-bold">Estimativa de Custos</span>
            </div>
          </div>
          <Badge variant="secondary">
            <Shield className="mr-1 h-3 w-3" />
            Admin
          </Badge>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 pb-[100px]">
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Parameters Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Parâmetros de Cálculo
              </CardTitle>
              <CardDescription>
                Ajuste os parâmetros para estimar custos. Valores padrão baseados nas médias da plataforma.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="questionSize">
                    Tamanho médio pergunta (chars)
                    {platformAverages.avgQuestionSizeChars && (
                      <span className="text-xs text-muted-foreground ml-2">
                        (média: {platformAverages.avgQuestionSizeChars})
                      </span>
                    )}
                  </Label>
                  <Input
                    id="questionSize"
                    type="number"
                    value={params.avgQuestionSizeChars}
                    onChange={(e) => setParams(p => ({ ...p, avgQuestionSizeChars: Number(e.target.value) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="answerSize">
                    Tamanho médio resposta (chars)
                    {platformAverages.avgAnswerSizeChars && (
                      <span className="text-xs text-muted-foreground ml-2">
                        (média: {platformAverages.avgAnswerSizeChars})
                      </span>
                    )}
                  </Label>
                  <Input
                    id="answerSize"
                    type="number"
                    value={params.avgAnswerSizeChars}
                    onChange={(e) => setParams(p => ({ ...p, avgAnswerSizeChars: Number(e.target.value) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contextSize">Tamanho contexto (chars)</Label>
                  <Input
                    id="contextSize"
                    type="number"
                    value={params.avgContextSizeChars}
                    onChange={(e) => setParams(p => ({ ...p, avgContextSizeChars: Number(e.target.value) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="questionsPerForm">
                    Perguntas por formulário
                    {platformAverages.avgQuestionsPerForm && (
                      <span className="text-xs text-muted-foreground ml-2">
                        (média: {platformAverages.avgQuestionsPerForm})
                      </span>
                    )}
                  </Label>
                  <Input
                    id="questionsPerForm"
                    type="number"
                    value={params.avgQuestionsPerForm}
                    onChange={(e) => setParams(p => ({ ...p, avgQuestionsPerForm: Number(e.target.value) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="leadsPerForm">
                    Leads por formulário
                    {platformAverages.avgLeadsPerForm && (
                      <span className="text-xs text-muted-foreground ml-2">
                        (média: {platformAverages.avgLeadsPerForm})
                      </span>
                    )}
                  </Label>
                  <Input
                    id="leadsPerForm"
                    type="number"
                    value={params.avgLeadsPerForm}
                    onChange={(e) => setParams(p => ({ ...p, avgLeadsPerForm: Number(e.target.value) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="formsPerUser">
                    Formulários por usuário
                    {platformAverages.avgFormsPerUser && (
                      <span className="text-xs text-muted-foreground ml-2">
                        (média: {platformAverages.avgFormsPerUser})
                      </span>
                    )}
                  </Label>
                  <Input
                    id="formsPerUser"
                    type="number"
                    value={params.avgFormsPerUser}
                    onChange={(e) => setParams(p => ({ ...p, avgFormsPerUser: Number(e.target.value) }))}
                  />
                </div>
              </div>

              <Separator />

              {/* Document Generation Params */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-muted-foreground">Geração do Documento Final</h4>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="docInput">
                    Input do documento (chars)
                    {platformAverages.avgDocInputChars && (
                      <span className="text-xs text-muted-foreground ml-2">
                        (média top 20: {platformAverages.avgDocInputChars})
                      </span>
                    )}
                  </Label>
                  <Input
                    id="docInput"
                    type="number"
                    value={params.avgDocInputChars}
                    onChange={(e) => setParams(p => ({ ...p, avgDocInputChars: Number(e.target.value) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="docOutput">
                    Output do documento (palavras)
                    {platformAverages.avgDocOutputWords && (
                      <span className="text-xs text-muted-foreground ml-2">
                        (padrão: {platformAverages.avgDocOutputWords})
                      </span>
                    )}
                  </Label>
                  <Input
                    id="docOutput"
                    type="number"
                    value={params.avgDocOutputWords}
                    onChange={(e) => setParams(p => ({ ...p, avgDocOutputWords: Number(e.target.value) }))}
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="margin">Margem de lucro (%)</Label>
                <Input
                  id="margin"
                  type="number"
                  value={params.profitMargin}
                  onChange={(e) => setParams(p => ({ ...p, profitMargin: Number(e.target.value) }))}
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSaveParams} className="flex-1">
                  <Save className="mr-2 h-4 w-4" />
                  Salvar Parâmetros
                </Button>
                <Button variant="outline" onClick={handleResetToAverages}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Resetar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Results Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Resultados do Cálculo
              </CardTitle>
              <CardDescription>
                Custos estimados baseados nos parâmetros configurados
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Cost Breakdown per Lead */}
              <div className="p-4 rounded-lg bg-muted/50 space-y-4">
                <h4 className="font-semibold">Custo por Lead (Breakdown)</h4>
                
                {/* Questions Cost */}
                <div className="pl-3 border-l-2 border-muted-foreground/20">
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    Geração de Perguntas ({params.avgQuestionsPerForm} perguntas)
                  </div>
                  <div className="text-sm">
                    ${leadCost.questionsCostUSD.toFixed(6)} USD / R$ {leadCost.questionsCostBRL.toFixed(4)}
                  </div>
                </div>

                {/* Document Cost */}
                <div className="pl-3 border-l-2 border-primary/50">
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    Geração do Documento Final
                  </div>
                  <div className="text-xs text-muted-foreground mb-1">
                    Input: {params.avgDocInputChars.toLocaleString()} chars → {Math.round(params.avgDocInputChars * TOKENS_PER_CHAR).toLocaleString()} tokens
                  </div>
                  <div className="text-xs text-muted-foreground mb-1">
                    Output: {params.avgDocOutputWords.toLocaleString()} palavras → {Math.round(params.avgDocOutputWords * AVG_CHARS_PER_WORD * TOKENS_PER_CHAR).toLocaleString()} tokens
                  </div>
                  <div className="text-sm">
                    ${leadCost.docCostUSD.toFixed(6)} USD / R$ {leadCost.docCostBRL.toFixed(4)}
                  </div>
                </div>

                {/* Total */}
                <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                  <div>
                    <div className="text-sm text-muted-foreground">Total Real</div>
                    <div className="text-lg font-bold">
                      ${leadCost.costUSD.toFixed(4)} USD
                    </div>
                    <div className="text-sm text-muted-foreground">
                      R$ {leadCost.costBRL.toFixed(4)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Com Margem ({params.profitMargin}%)</div>
                    <div className="text-lg font-bold text-primary">
                      ${calculateWithMargin(leadCost.costUSD).toFixed(4)} USD
                    </div>
                    <div className="text-sm text-muted-foreground">
                      R$ {calculateWithMargin(leadCost.costBRL).toFixed(4)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Monthly User Cost */}
              <div className="p-4 rounded-lg bg-muted/50">
                <h4 className="font-semibold mb-2">Custo Mensal por Usuário</h4>
                <p className="text-xs text-muted-foreground mb-2">
                  ({params.avgLeadsPerForm} leads × {params.avgFormsPerUser} forms = {params.avgLeadsPerForm * params.avgFormsPerUser} leads/mês)
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Custo Real</div>
                    <div className="text-lg font-bold">
                      ${monthlyUserCost.costUSD.toFixed(4)} USD
                    </div>
                    <div className="text-sm text-muted-foreground">
                      R$ {monthlyUserCost.costBRL.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Com Margem ({params.profitMargin}%)</div>
                    <div className="text-lg font-bold text-primary">
                      ${calculateWithMargin(monthlyUserCost.costUSD).toFixed(4)} USD
                    </div>
                    <div className="text-sm text-muted-foreground">
                      R$ {calculateWithMargin(monthlyUserCost.costBRL).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing Suggestions */}
              <div className="p-4 rounded-lg border border-primary/20 bg-primary/5">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary" />
                  Sugestões de Precificação
                </h4>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span>Sugestão leads Pro (R$ 29,90/mês):</span>
                    <span className="font-bold text-muted-foreground">
                      {suggestedProLeads} leads
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sugestão preço 50 leads avulsos:</span>
                    <span className="font-bold text-muted-foreground">
                      R$ {(suggestedLooseLeadsPrice50 / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Editable Stripe Settings */}
              <div className="p-4 rounded-lg border border-accent/30 bg-accent/5">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Settings className="h-4 w-4 text-accent" />
                  Configurações do Stripe (Editável)
                </h4>
                
                {/* Pro Plan Lead Limit */}
                <div className="space-y-3 mb-4">
                  <div className="space-y-2">
                    <Label htmlFor="proLeadLimit" className="text-sm">
                      Leads inclusos no Plano Pro (R$ 29,90/mês)
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="proLeadLimit"
                        type="number"
                        value={proLeadLimit}
                        onChange={(e) => setProLeadLimit(Number(e.target.value))}
                        className="flex-1"
                      />
                      <Button 
                        onClick={handleSaveProPlan} 
                        disabled={savingProPlan}
                        size="sm"
                      >
                        {savingProPlan ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Custo real: R$ {(proLeadLimit * leadCost.costBRL).toFixed(2)} | 
                      Lucro: R$ {(29.90 - proLeadLimit * leadCost.costBRL).toFixed(2)}
                      {proLeadLimit * leadCost.costBRL > 29.90 && (
                        <span className="text-destructive ml-1">(prejuízo!)</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Loose Leads Price */}
                <div className="space-y-2">
                  <Label htmlFor="looseLeadsPrice" className="text-sm">
                    Preço de 100 leads avulsos (centavos)
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="looseLeadsPrice"
                      type="number"
                      value={looseLeadsPrice}
                      onChange={(e) => setLooseLeadsPrice(Number(e.target.value))}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleSaveLooseLeads} 
                      disabled={savingLooseLeads}
                      size="sm"
                    >
                      {savingLooseLeads ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Preço final: R$ {(looseLeadsPrice / 100).toFixed(2)} por 100 leads | 
                    Custo real: R$ {(100 * leadCost.costBRL).toFixed(2)} | 
                    Lucro: R$ {(looseLeadsPrice / 100 - 100 * leadCost.costBRL).toFixed(2)}
                    {looseLeadsPrice / 100 < 100 * leadCost.costBRL && (
                      <span className="text-destructive ml-1">(prejuízo!)</span>
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top 20 Users Table */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Top 20 Usuários por Custo Estimado
            </CardTitle>
            <CardDescription>
              Custo total da plataforma (top 20): ${totalPlatformCost.toFixed(2)} USD / R$ {(totalPlatformCost * USD_TO_BRL).toFixed(2)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Total Leads</TableHead>
                  <TableHead className="text-right">Custo USD</TableHead>
                  <TableHead className="text-right">Custo BRL</TableHead>
                  <TableHead className="text-right">Com Margem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : topUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Nenhum usuário encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  topUsers.map((user, index) => (
                    <TableRow key={user.email}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell className="text-right">{user.totalLeads}</TableCell>
                      <TableCell className="text-right">${user.estimatedCostUSD.toFixed(4)}</TableCell>
                      <TableCell className="text-right">R$ {user.estimatedCostBRL.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-bold text-primary">
                        R$ {calculateWithMargin(user.estimatedCostBRL).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
